import qs from 'qs'

import type {
  IAddToClusterResponse,
  IBatchAddToClusterPayload,
  IBatchAddToClusterResponse,
  IBatchDeleteFromClusterPayload,
  IClusterApp,
  TClusterForm,
  ICluster,
  ICreateClusterPayload,
  ICreateClusterResponse,
  IGetClusterCandidatesParams,
  IGetClusterResponse,
  IListAppsForClusterPayload,
  IListClustersParams,
  ISetTagsResponse,
  IUpdateClusterPayload,
  IGetClusterFormPayload,
  IBatchAddTagsPayload,
  TSetVideosPayload,
  IClustersAllForms,
  IClusterFormPayload,
  IClusterFormUpdatePayload,
  IRecommendedClusterList,
  TClusterFormTemplate,
  IAppCompetitor,
  IGetAppCompetitorsPayload,
} from '~/types/cluster'
import type { DeviceType, MobileDeviceType } from '~/types/device-type'
import type { ICompanyList, TCompaniesListParams } from '~/types/ranking'

import config from '~/config'
import { paramsSerializer } from '~/utils/api'
import { request } from '~/utils/request'

/**@doc https://clusters-dev-europe-west1-hu4ui3wkoq-ew.a.run.app/#get-/clusters/-clusterID-/apps/-storeID-/device-type/-deviceType-/category/-categoryID-/form */
export const getClusterForm = ({
  categoryId,
  clusterId,
  deviceType,
  storeId,
}: IGetClusterFormPayload): Promise<TClusterForm[]> => {
  return request({
    url: `${config.CLUSTERS_API}/clusters/${clusterId}/apps/${storeId}/device-type/${deviceType}/category/${categoryId}/form`,
  })
}

/** @doc https://clusters-dev-europe-west1-hu4ui3wkoq-ew.a.run.app/#get-/clusters/-clusterID-/stats */
export const getClusterStats = (clusterId: string): Promise<ICluster> => {
  return request({
    url: `${config.CLUSTERS_API}/clusters/${clusterId}/stats`,
  })
}

/** @doc https://clusters-dev-europe-west1-hu4ui3wkoq-ew.a.run.app/#get-/clusters/stats/all */
export const getGlobalStats = (): Promise<ICluster> => {
  return request({
    url: `${config.CLUSTERS_API}/clusters/stats/all`,
  })
}

/** @doc https://clusters-dev-europe-west1-hu4ui3wkoq-ew.a.run.app/#delete-/clusters/-clusterID- */
export const deleteCluster = async ({ clusterId }: { clusterId: string }): Promise<void> => {
  return request({
    method: 'DELETE',
    url: `${config.CLUSTERS_API}/clusters/${clusterId}`,
  })
}

/** @doc https://clusters-dev-europe-west1-hu4ui3wkoq-ew.a.run.app/#get-/apps/store-id/-storeID-/device-type/-deviceType-/competitors */
export const getAppCompetitors = async ({
  deviceType,
  limit,
  storeId,
}: IGetAppCompetitorsPayload): Promise<IAppCompetitor[]> => {
  const formattedParams = { limit }

  return request({
    method: 'GET',
    url: `${config.CLUSTERS_API}/apps/store-id/${storeId}/device-type/${deviceType}/competitors?${paramsSerializer(
      formattedParams
    )}`,
  })
}

/**
 * Light version of listClusters not paginated and without filters
 */
export const allClusters = async (): Promise<{ cluster_id: string; name: string }[]> => {
  return request({
    url: `${config.CLUSTERS_API}/clusters/light`,
  })
}

/** @doc https://clusters-dev-europe-west1-hu4ui3wkoq-ew.a.run.app/#get-/clusters */
export const listClusters = async (params?: IListClustersParams): Promise<IPaginatedResponse<ICluster>> => {
  const formattedParams = {
    name: params?.name,
    'op-filtering': params?.opFiltering,
    'order-by': params?.orderBy,
    page: params?.page || 0,
    q: params?.q,
    size: params?.size,
  }

  return request({
    url: `${config.CLUSTERS_API}/clusters?${qs.stringify(formattedParams)}`,
  })
}

/** @doc https://clusters-dev-europe-west1-hu4ui3wkoq-ew.a.run.app/#get-/clusters/-clusterID- */
export const getCluster = async (clusterId: string): Promise<IGetClusterResponse> => {
  return request({
    url: `${config.CLUSTERS_API}/clusters/${clusterId}`,
  })
}

/** @doc https://clusters-dev-europe-west1-hu4ui3wkoq-ew.a.run.app/#get-/clusters/-clusterID-/apps/similar */
export const getClusterCandidates = async (
  clusterId: string,
  params?: IGetClusterCandidatesParams
): Promise<IPaginatedResponse<IClusterApp> & { total_filtered: number }> => {
  const formattedParams = {
    'device-type': params?.deviceType,
    'min-score': params?.minScore,
    offset: params?.offset,
    'op-filtering': params?.opFiltering,
    'order-by': params?.orderBy,
    page: params?.page || 0,
    q: params?.q,
    'search-elements': params?.searchElements,
    size: params?.size,
  }

  return request({
    url: `${config.CLUSTERS_API}/clusters/${clusterId}/apps/similar?${paramsSerializer(formattedParams)}`,
  })
}

/** @doc https://clusters-dev-europe-west1-hu4ui3wkoq-ew.a.run.app/#post-/clusters */
export const createCluster = ({
  description,
  features,
  highVolumeThreshold,
  isGetKeywordsEnabled,
  isGetReviewsEnabled,
  name,
  nbLeaders,
  status,
  storeIds,
  tags,
  userId,
}: ICreateClusterPayload): Promise<ICreateClusterResponse> => {
  return request({
    data: {
      description,
      features,
      get_keywords_enabled: isGetKeywordsEnabled,
      get_reviews_enabled: isGetReviewsEnabled,
      high_volume_threshold: highVolumeThreshold,
      name,
      nb_leaders: nbLeaders,
      status,
      store_ids: storeIds,
      tags,
      user_id: userId,
    },
    method: 'POST',
    url: `${config.CLUSTERS_API}/clusters`,
  })
}

/** @doc https://clusters-dev-europe-west1-hu4ui3wkoq-ew.a.run.app/#get-/apps/clusters/-clusterID- */
export const listAppsForCluster = (
  payload: IListAppsForClusterPayload
): Promise<IPaginatedResponse<IClusterApp> & { total_filtered: number }> => {
  const { clusterId } = payload

  const formattedParams = {
    'device-type': payload.deviceType,
    'filter-preset': payload.filterPreset,
    'op-filtering': payload.opFiltering,
    'order-by': payload.orderBy,
    page: payload.page || 0,
    q: payload.q,
    size: payload.size,
    tags: payload.tags,
  }

  return request({
    method: 'GET',
    url: `${config.CLUSTERS_API}/apps/clusters/${clusterId}?${paramsSerializer(formattedParams)}`,
  })
}

/** @doc https://clusters-dev-europe-west1-hu4ui3wkoq-ew.a.run.app/#get-/apps/store-id/-storeID-/device-type/-deviceType- */
export const getClusterApp = async (
  clusterId: string,
  deviceType: DeviceType,
  storeId: string
): Promise<IClusterApp> => {
  return request({
    url: `${config.CLUSTERS_API}/clusters/${clusterId}/apps/store-id/${storeId}/device-type/${deviceType}`,
  })
}

/** @doc https://clusters-dev-europe-west1-hu4ui3wkoq-ew.a.run.app/#post-/apps/clusters/-clusterID-/add-by-url */
export const addToClusterUsingUrl = ({
  clusterId,
  storeUrl,
}: {
  clusterId: string
  storeUrl: string
}): Promise<IAddToClusterResponse> => {
  return request({
    data: {
      store_url: storeUrl,
    },
    method: 'POST',
    url: `${config.CLUSTERS_API}/apps/clusters/${clusterId}/add-by-url`,
  })
}

/** @doc https://clusters-dev-europe-west1-hu4ui3wkoq-ew.a.run.app/#post-/apps/clusters/-clusterID-/batch */
export const batchAddToCluster = (payload: IBatchAddToClusterPayload): Promise<IBatchAddToClusterResponse> => {
  const { clusterId, items } = payload

  return request({
    data: items.map(({ deviceType, isTarget, storeId }) => ({
      device_type: deviceType,
      is_target: isTarget,
      store_id: storeId,
    })),
    method: 'POST',
    url: `${config.CLUSTERS_API}/apps/clusters/${clusterId}/batch`,
  })
}

/** @doc https://clusters-dev-europe-west1-hu4ui3wkoq-ew.a.run.app/#post-/apps/clusters/-clusterID-/delete-batch */
export const batchDeleteFromCluster = (payload: IBatchDeleteFromClusterPayload): Promise<void> => {
  const { clusterId, items } = payload

  return request({
    data: items.map(({ deviceType, storeId }) => ({
      device_type: deviceType,
      store_id: storeId,
    })),
    method: 'POST',
    url: `${config.CLUSTERS_API}/apps/clusters/${clusterId}/delete-batch`,
  })
}

/** @doc https://clusters-dev-europe-west1-hu4ui3wkoq-ew.a.run.app/#put-/clusters/-clusterID- */
export const updateCluster = ({
  clusterId,
  description,
  features,
  highVolumeThreshold,
  isGetKeywordsEnabled,
  isGetReviewsEnabled,
  name,
  nbLeaders,
  status,
  tags,
  userId,
}: IUpdateClusterPayload): Promise<void> => {
  return request({
    data: {
      description,
      features,
      get_keywords_enabled: isGetKeywordsEnabled,
      get_reviews_enabled: isGetReviewsEnabled,
      high_volume_threshold: highVolumeThreshold,
      name,
      nb_leaders: nbLeaders,
      status,
      tags,
      user_id: userId,
    },
    method: 'PUT',
    url: `${config.CLUSTERS_API}/clusters/${clusterId}`,
  })
}

export const addInfosToCluster = ({
  categoryId,
  clusterId,
  deviceType,
  fields,
  storeId,
}: {
  categoryId: string
  clusterId: string
  deviceType: string
  fields: any
  storeId: string
}): Promise<void> => {
  return request({
    data: { fields },
    method: 'POST',
    url: `${config.CLUSTERS_API}/clusters/${clusterId}/apps/${storeId}/device-type/${deviceType}/category/${categoryId}/form`,
  })
}

/** @doc https://clusters-dev-europe-west1-hu4ui3wkoq-ew.a.run.app/#get-/clusters/-clusterID-/tags */
export const getTags = (clusterId: string): Promise<string[]> => {
  return request({
    url: `${config.CLUSTERS_API}/clusters/${clusterId}/tags`,
  })
}

/** @doc https://clusters-dev-europe-west1-hu4ui3wkoq-ew.a.run.app/#put-/apps/clusters/-clusterID-/store-id/-storeID-/device-type/-deviceType-/tags */
export const setTags = ({
  clusterId,
  deviceType,
  storeId,
  tags,
}: {
  clusterId: string
  deviceType: string
  storeId: string
  tags: string[]
}): Promise<ISetTagsResponse> => {
  return request({
    data: tags,
    method: 'PUT',
    url: `${config.CLUSTERS_API}/apps/clusters/${clusterId}/store-id/${storeId}/device-type/${deviceType}/tags`,
  })
}

/** @doc https://clusters-dev-europe-west1-hu4ui3wkoq-ew.a.run.app/#post-/clusters/-clusterID-/app/tags/batch-add */
export const batchAddTags = ({ apps, clusterId, tags }: IBatchAddTagsPayload): Promise<void> => {
  return request({
    data: {
      apps: apps.map(({ deviceType, storeId }) => ({ device_type: deviceType, store_id: storeId })),
      tags,
    },
    method: 'POST',
    url: `${config.CLUSTERS_API}/clusters/${clusterId}/app/tags/batch-add`,
  })
}

/**@doc https://clusters-dev-europe-west1-hu4ui3wkoq-ew.a.run.app/#get-/companies-stats */

export const getCompaniesList = (params?: TCompaniesListParams): Promise<IPaginatedResponse<ICompanyList>> => {
  return request({
    params,
    url: `${config.CLUSTERS_API}/companies-stats`,
  })
}

/**@doc https://clusters-dev-europe-west1-hu4ui3wkoq-ew.a.run.app/#get-/apps/recommended-clusters */
export const getRecommendedClustersList = (
  params?: TCompaniesListParams
): Promise<IPaginatedResponse<IRecommendedClusterList>> =>
  request({
    params,
    url: `${config.CLUSTERS_API}/apps/recommended-clusters`,
  })

/** @doc https://clusters-dev-europe-west1-hu4ui3wkoq-ew.a.run.app/#get-/clusters/tags/all */
export const getClustersTags = (): Promise<string[]> => {
  return request({
    url: `${config.CLUSTERS_API}/clusters/tags/all`,
  })
}

/** @doc https://clusters-dev-europe-west1-hu4ui3wkoq-ew.a.run.app/#put-/clusters/-clusterID-/tags */
export const setClusterTags = ({ clusterId, tags }: { clusterId: string; tags: string[] }): Promise<void> => {
  return request({
    data: tags,
    method: 'PUT',
    url: `${config.CLUSTERS_API}/clusters/${clusterId}/tags`,
  })
}

/** @doc https://clusters-dev-europe-west1-hu4ui3wkoq-ew.a.run.app/#put-/apps/store-id/-storeID-/device-type/-deviceType-/videos */
export const setAppVideos = ({ deviceType, storeId, videoIds }: TSetVideosPayload): Promise<void> => {
  return request({
    data: videoIds,
    method: 'PUT',
    url: `${config.CLUSTERS_API}/apps/store-id/${storeId}/device-type/${deviceType}/videos`,
  })
}

/** @doc https://clusters-dev-europe-west1-hu4ui3wkoq-ew.a.run.app/#get-/clusters/-clusterID-/apps/-storeID-/device-type/-deviceType-/form */
export const getAllForms = ({ clusterId, deviceType, storeId }: IClusterFormPayload): Promise<IClustersAllForms> => {
  return request({
    url: `${config.CLUSTERS_API}/clusters/${clusterId}/apps/${storeId}/device-type/${deviceType}/form`,
  })
}

/** @doc https://clusters-dev-europe-west1-hu4ui3wkoq-ew.a.run.app/#post-/clusters/-clusterID-/apps/-storeID-/device-type/-deviceType-/form */
export const partialSaveInfosForm = ({
  clusterId,
  deviceType,
  fields,
  storeId,
}: IClusterFormUpdatePayload): Promise<void> => {
  return request({
    data: { fields },
    method: 'POST',
    url: `${config.CLUSTERS_API}/clusters/${clusterId}/apps/${storeId}/device-type/${deviceType}/form`,
  })
}

/** @doc https://clusters-dev-europe-west1-hu4ui3wkoq-ew.a.run.app/#get-/categories/-categoryID-/form/template */
export const getFormTemplate = (categoryId: string): Promise<TClusterFormTemplate[]> => {
  return request({
    url: `${config.CLUSTERS_API}/categories/${categoryId}/form/template`,
  })
}

/** @doc https://clusters-dev-europe-west1-hu4ui3wkoq-ew.a.run.app/#post-/categories/-categoryID-/form/template */
export const saveFormTemplate = ({
  categoryId,
  data,
}: {
  categoryId: string
  data: TClusterFormTemplate[]
}): Promise<void> => {
  return request({
    data,
    method: 'POST',
    url: `${config.CLUSTERS_API}/categories/${categoryId}/form/template`,
  })
}

/** @doc https://clusters-dev-europe-west1-hu4ui3wkoq-ew.a.run.app/#get-/clusters/-clusterID-/form/categories/features/template */
export const getClusterFeatures = (clusterId: string): Promise<TClusterFormTemplate[]> => {
  return request({
    url: `${config.CLUSTERS_API}/clusters/${clusterId}/form/categories/features/template`,
  })
}

export interface IClusterFormTemplatePayload extends Omit<TClusterFormTemplate, 'select_options'> {
  select_options: { key: string; score: number }[]
}

/** https://clusters-dev-europe-west1-hu4ui3wkoq-ew.a.run.app/#post-/clusters/-clusterID-/form/categories/features/template */
export const saveClusterFeatures = ({
  clusterId,
  data,
}: {
  clusterId: string
  data: IClusterFormTemplatePayload[]
}): Promise<void> => {
  return request({
    data,
    method: 'POST',
    url: `${config.CLUSTERS_API}/clusters/${clusterId}/form/categories/features/template`,
  })
}

/** @doc https://clusters-dev-europe-west1-hu4ui3wkoq-ew.a.run.app/#post-/apps/recommended-clusters/action */
export const setRecommendedCluster = ({
  clusterId,
  deviceType,
  status,
  storeId,
}: {
  clusterId?: string
  deviceType: MobileDeviceType
  status: string
  storeId: string
}): Promise<void> => {
  return request({
    data: {
      device_type: deviceType,
      selected_cluster_id: clusterId,
      status,
      store_id: storeId,
    },
    method: 'POST',
    url: `${config.CLUSTERS_API}/apps/recommended-clusters/action`,
  })
}
